import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CardActionArea from '@mui/material/CardActionArea';
import { useTranslation } from 'react-i18next';
import { Search, MoreVert, Add, Menu } from '@mui/icons-material';
import { NoteSummary } from '@common/schemas/notes.schema';

interface NoteListProps {
  notes: NoteSummary[];
  selectedNoteId: string | null;
  onSelectNote: (noteId: string) => void;
  onCreateNote: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isMobile?: boolean;
  onOpenSidebar?: () => void;
}

export function NoteList({
  notes,
  selectedNoteId,
  onSelectNote,
  onCreateNote,
  searchQuery,
  onSearchChange,
  isMobile,
  onOpenSidebar,
}: NoteListProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header and Search */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && onOpenSidebar && (
              <IconButton size="small" onClick={onOpenSidebar} sx={{ mr: 1, ml: -1 }}>
                <Menu fontSize="small" />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {t('research_notes.collections.all_notes')}
            </Typography>
          </Box>
          <Tooltip title={t('research_notes.new_note')}>
            <IconButton size="small" onClick={onCreateNote} color="primary">
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={t('research_notes.search_placeholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            sx: { borderRadius: 2, bgcolor: 'background.default' }
          }}
        />
      </Box>

      {/* List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
        {notes.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">{t('research_notes.collections.no_notes_found')}</Typography>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1 
          }}>
            {notes.map(note => {
              const isSelected = selectedNoteId === note.id;
              
              const dateObj = new Date(note.updatedAt || 0);
              const isToday = new Date().toDateString() === dateObj.toDateString();
              const dateString = isToday 
                ? dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : dateObj.toLocaleDateString();

              return (
                <CardActionArea
                  key={note.id}
                  onClick={() => onSelectNote(note.id)}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    '&:hover': {
                      bgcolor: isSelected ? 'action.selected' : 'action.hover',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600, 
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {note.title || t('research_notes.editor.untitled')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                      {dateString}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: '0.8125rem',
                      lineHeight: 1.4,
                    }}
                  >
                    {note.preview || t('research_notes.collections.no_additional_text')}
                  </Typography>
                  
                  {note.tags && note.tags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                      {note.tags.slice(0, 2).map(tag => (
                        <Box 
                          key={tag} 
                          sx={{ 
                            fontSize: '0.65rem', 
                            px: 1, 
                            py: 0.25, 
                            bgcolor: 'action.hover', 
                            borderRadius: 1,
                            color: 'text.secondary',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                      {note.tags.length > 2 && (
                        <Box sx={{ fontSize: '0.65rem', color: 'text.secondary', px: 0.5 }}>
                          +{note.tags.length - 2}
                        </Box>
                      )}
                    </Box>
                  )}
                </CardActionArea>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
